import React from 'react'
import { Allocation, HomeMetaProps } from '../data/types'
import { CalendarIcon, LocationIcon, PersonIcon, PhoneIcon } from '../icons'
import { Card } from './ui'
import { formatDateOnly } from '../utils/dates'

type Props = {
    allocation: Allocation
    meta: Pick<HomeMetaProps, 'areaLabel' | 'shiftLabel' | 'shiftLeadLabel'>
}

export const ShiftAllocationCard = (props: Props) => {
    const { allocation, meta } = props

    const formatDate = (dateStr: string | undefined) => {
        return formatDateOnly(dateStr, 'en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        })
    }

    const event = allocation.shift?.area?.event
    const area = allocation.shift?.area
    const shift = allocation.shift

    const shiftLead = shift?.lead ?? area?.lead

    return (
        <Card className="hover:shadow-lg transition-shadow">
            <div className="p-4">
                {event && (
                    <div className="mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
                            {event.name}
                        </h3>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-400">
                            <div className="flex items-center gap-1">
                                <LocationIcon className="w-4 h-4" />
                                <span>{event.location}</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <CalendarIcon className="w-4 h-4" />
                                <span>{formatDate(event.start_date)}</span>
                            </div>
                        </div>
                    </div>
                )}

                <div className="mb-3">
                    <span className="text-sm text-gray-500 dark:text-gray-400">{meta.areaLabel}:</span>
                    <span className="ms-2 font-medium text-gray-900 dark:text-gray-100">
                        {area?.name || 'Unknown Area'}
                    </span>
                </div>

                <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
                    <span className="text-gray-500 dark:text-gray-400">
                        {meta.shiftLabel}:
                    </span>

                    <span className="font-medium text-gray-900 dark:text-gray-100">
                        {shift?.title ?? shift?.duty?.name}
                    </span>
                </div>

                {shift?.criteria && (
                    <div className="mb-3">
                        <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{shift.criteria}</p>
                    </div>
                )}

                {shiftLead && (
                    <div className="mb-3 p-3 rounded-lg bg-gray-100 dark:bg-gray-900">
                        <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">{meta.shiftLeadLabel}</div>
                        <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <PersonIcon className="w-4 h-4" />
                            <span className="font-medium">{shiftLead.first_name} {shiftLead.last_name}</span>
                        </div>
                        {shiftLead.phone && (
                            <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 mt-1">
                                <PhoneIcon className="w-4 h-4" />
                                <a href={`tel:${shiftLead.phone}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                                    {shiftLead.phone}
                                </a>
                            </div>
                        )}
                    </div>
                )}

            </div>
        </Card>
    )
}
